import React, { lazy, Suspense } from "react";
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import DestinationMarquee from "./components/DestinationMarquee";
import PageTransition from "./components/PageTransition";
import ScrollToTop from "./components/ScrollToTop";
import Landing from "./pages/Landing";


// Lazy-load with auto-reload on stale chunk failures (e.g. after a deploy or HMR restart)
const lazyWithReload = <T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
  lazy(() =>
    factory().catch((err) => {
      const msg = String(err?.message || err);
      if (/Failed to fetch dynamically imported module|dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch/i.test(msg)) {
        const key = "__lovable_chunk_reloaded__";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          // Return a never-resolving promise while reload happens
          return new Promise(() => {}) as Promise<{ default: T }>;
        }
      }
      throw err;
    })
  );

const Quiz = lazyWithReload(() => import("./pages/Quiz"));
const Results = lazyWithReload(() => import("./pages/Results"));
const Profile = lazyWithReload(() => import("./pages/Profile"));
const Auth = lazyWithReload(() => import("./pages/Auth"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazyWithReload(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithReload(() => import("./pages/TermsOfService"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const AdminInsights = lazyWithReload(() => import("./pages/AdminInsights"));
const SharedDeck = lazyWithReload(() => import("./pages/SharedDeck"));
const PersonalityPreview = lazyWithReload(() => import("./pages/PersonalityPreview"));


const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
        <Route path="/quiz" element={<PageTransition><Quiz /></PageTransition>} />
        <Route path="/results/:id" element={<PageTransition><Results /></PageTransition>} />
        <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
        <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
        <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
        <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
        <Route path="/admin/insights" element={<PageTransition><AdminInsights /></PageTransition>} />
        <Route path="/deck/:slug" element={<PageTransition><SharedDeck /></PageTransition>} />
        <Route path="/dev/personality-preview" element={<PageTransition><PersonalityPreview /></PageTransition>} />
        
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <DestinationMarquee />
          <main className="flex-1">
            <Suspense fallback={<div className="flex-1" />}>
              <AnimatedRoutes />
            </Suspense>
          </main>
          <Footer />
          <CookieConsent />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
